import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { find, includes, isEmpty as lodashIsEmpty, some } from 'lodash'

import { Label } from '@/components/Text/Text'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ReminderStatus, ThresholdUnit } from '@/schema'

import { MAX_REMINDER_NAME_LENGTH } from '../constants'
import type {
  ReminderConfiguration,
  ReminderFormData,
  ReminderValidationError,
} from '../types'

interface EditReminderFormProps {
  reminderName?: string
  reminderStatus?: string
  reminderUnit?: string
  reminderValue?: number
  onSave: (data: ReminderFormData) => void
  onCancel: () => void
  existingNames?: string[]
  existingReminders?: ReminderConfiguration[]
  isLoading?: boolean
}

export const EditReminderForm: React.FC<EditReminderFormProps> = ({
  reminderName = '',
  reminderStatus = ReminderStatus.Active,
  reminderUnit = ThresholdUnit.Days,
  reminderValue,
  onSave,
  onCancel,
  existingNames = [],
  existingReminders = [],
  isLoading = false,
}) => {
  const { t } = useTranslation()
  const [name, setName] = useState(reminderName)
  const [value, setValue] = useState(reminderValue?.toString() || '')
  const [errors, setErrors] = useState<ReminderValidationError[]>([])

  const isEditMode = reminderName !== ''

  useEffect(() => {
    setName(reminderName)
    setValue(reminderValue?.toString() || '')
    setErrors([])
  }, [reminderName, reminderValue])

  const validate = (): boolean => {
    const newErrors: ReminderValidationError[] = []

    if (!name.trim()) {
      newErrors.push({
        field: 'name',
        message: t('workflowConfig.validation.nameRequired'),
      })
    } else if (name.length > MAX_REMINDER_NAME_LENGTH) {
      newErrors.push({
        field: 'name',
        message: t('workflowConfig.validation.nameTooLong', {
          max: MAX_REMINDER_NAME_LENGTH,
        }),
      })
    } else if (
      includes(existingNames, name.trim()) &&
      name.trim() !== reminderName.trim()
    ) {
      newErrors.push({
        field: 'name',
        message: t('workflowConfig.validation.nameExists'),
      })
    }

    const parsedValue = Number.parseInt(value, 10)
    if (!value.trim() || Number.isNaN(parsedValue) || parsedValue <= 1) {
      newErrors.push({
        field: 'value',
        message: t('workflowConfig.validation.daysInvalid'),
      })
    } else {
      const isDuplicateThreshold = some(
        existingReminders,
        (r) =>
          r.unit === reminderUnit &&
          r.value === parsedValue &&
          !(r.name === reminderName && r.value === reminderValue)
      )

      if (isDuplicateThreshold) {
        newErrors.push({
          field: 'value',
          message: t('workflowConfig.validation.daysExists'),
        })
      }
    }

    setErrors(newErrors)
    return lodashIsEmpty(newErrors)
  }

  const handleSave = () => {
    if (validate()) {
      onSave({
        name: name.trim(),
        status: reminderStatus,
        unit: reminderUnit,
        value: Number.parseInt(value, 10),
      })
    }
  }

  const handleCancel = () => {
    setErrors([])
    onCancel()
  }

  const getFieldError = (
    field: 'name' | 'value' | 'unit'
  ): string | undefined => {
    return find(errors, { field })?.message
  }

  const formContent = (
    <>
      <div className="mb-6">
        <h3 className="m-0 text-lg font-semibold text-gray-light-900">
          {isEditMode
            ? t('workflowConfig.editReminder.title')
            : t('workflowConfig.reminders')}
        </h3>
        <p className="m-0 mt-1 text-sm text-gray-light-600">
          {t('workflowConfig.editReminder.subtitle')}
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <Label>{t('workflowConfig.form.nameLabel')}</Label>
          <Input
            id="reminder-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('workflowConfig.form.namePlaceholder')}
            className="mt-1"
            maxLength={MAX_REMINDER_NAME_LENGTH}
          />
          {getFieldError('name') && (
            <p className="mt-1 text-sm text-red-600">{getFieldError('name')}</p>
          )}
        </div>

        <div>
          <Label>{t('workflowConfig.form.daysLabel')}</Label>
          <Input
            id="reminder-value"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={t('workflowConfig.form.daysPlaceholder')}
            className="mt-1"
          />
          {getFieldError('value') && (
            <p className="mt-1 text-sm text-red-600">
              {getFieldError('value')}
            </p>
          )}
        </div>
      </div>

      <div className="flex gap-3 mt-6">
        <Button
          variant="outlined"
          color="default"
          onClick={handleCancel}
          disabled={isLoading}
          className={isEditMode ? 'flex-1' : 'w-16 text-secondary-700'}
        >
          {t('common.cancel')}
        </Button>
        <Button
          variant="solid"
          color="primary"
          onClick={handleSave}
          disabled={isLoading}
          className={isEditMode ? 'flex-1' : 'w-16'}
        >
          {isEditMode ? t('common.update') : t('common.add')}
        </Button>
      </div>
    </>
  )

  return (
    <div className="p-6 bg-white border border-solid rounded-lg border-gray-light-300">
      {formContent}
    </div>
  )
}
